import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Layout, Server, Database, GitBranch, ArrowRight } from "lucide-react";

const skillCategories = [
  {
    num: "01",
    title: "Frontend Development",
    icon: <Layout size={20} strokeWidth={1.8} />,
    description:
      "Creating responsive, interactive user interfaces that are fast and intuitive.",
  },
  {
    num: "02",
    title: "Backend Development",
    icon: <Server size={20} strokeWidth={1.8} />,
    description:
      "Building scalable server-side architecture with clean, maintainable code.",
  },
  {
    num: "03",
    title: "Database Management",
    icon: <Database size={20} strokeWidth={1.8} />,
    description:
      "Designing efficient data storage solutions for performance and reliability.",
  },
  {
    num: "04",
    title: "API Integration",
    icon: <GitBranch size={20} strokeWidth={1.8} />,
    description:
      "Connecting systems and services seamlessly through well-structured APIs.",
  },
];

export default function AboutPreview() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-1.5 rounded-full mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
            <span className="text-blue-600 text-xs sm:text-sm font-medium tracking-wide">
              What I do
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-black mb-4">
            My <span className="text-blue-600">expertise</span>
          </h2>
          <p className="text-gray-500 text-sm sm:text-base md:text-lg leading-relaxed max-w-lg mx-auto">
            Building complete digital solutions from the ground up frontend
            interfaces to backend systems and databases.
          </p>
        </div>

        {/* Grid */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 border border-gray-100 rounded-2xl overflow-hidden divide-y sm:divide-y-0 sm:divide-x divide-gray-100 mb-6 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {skillCategories.map((category, index) => (
            <div
              key={category.title}
              className={`group p-7 md:p-8 bg-white hover:bg-blue-50/40 transition-colors duration-200 ${
                index >= 2 ? "border-t border-gray-100" : ""
              }`}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  {category.icon}
                </div>
                <div>
                  <p className="text-[11px] md:text-xs font-medium text-blue-300 tracking-widest mb-0.5">
                    {category.num}
                  </p>
                  <h3 className="text-[15px] sm:text-base md:text-lg font-medium text-black">
                    {category.title}
                  </h3>
                </div>
              </div>
              <p className="text-sm sm:text-[15px] md:text-base text-gray-500 leading-relaxed pl-14 md:pl-16">
                {category.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Bar */}
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-6 py-5 border border-gray-100 rounded-2xl transition-all duration-700 delay-400 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-sm sm:text-base md:text-[15px] text-gray-500">
            <span className="font-medium text-black">Want to know more?</span>{" "}
            Learn about my background and experience.
          </p>
          <Link
            to="/about"
            className="group inline-flex items-center gap-2 bg-black text-white text-sm md:text-base font-medium px-5 md:px-6 py-2.5 md:py-3 rounded-full hover:bg-blue-600 transition-colors whitespace-nowrap"
          >
            About me
            <ArrowRight
              size={14}
              className="group-hover:translate-x-0.5 transition-transform"
            />
          </Link>
        </div>
      </div>
    </section>
  );
}
